import { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Star, Trash2, MessageSquare, Search } from "lucide-react";
import { ExportMenu } from "@/components/ExportMenu";
import { TableSkeleton } from "@/components/admin/AdminSkeletons";

const Stars = ({ n }) => (
  <span className="flex items-center gap-0.5" aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`w-3.5 h-3.5 ${i <= n ? "fill-gold text-gold" : "text-muted-foreground/30"}`} aria-hidden="true" />)}
  </span>
);

export const ReviewsManager = () => {
  const [reviews, setReviews] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Permanently delete this review? The product rating will be recomputed.")) return;
    setBusy(id);
    try { await api.delete(`/admin/reviews/${id}`); toast.success("Review deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const filtered = useMemo(() => {
    if (!reviews) return [];
    const s = q.trim().toLowerCase();
    return reviews.filter((r) => !s || (r.product_title || "").toLowerCase().includes(s) || (r.user_name || "").toLowerCase().includes(s) || (r.comment || "").toLowerCase().includes(s));
  }, [reviews, q]);

  const cols = [
    { header: "Product", accessor: (r) => r.product_title },
    { header: "Buyer", accessor: (r) => r.user_name },
    { header: "Rating", accessor: (r) => r.rating },
    { header: "Comment", accessor: (r) => r.comment },
    { header: "Date", accessor: (r) => new Date(r.created_at).toLocaleDateString() },
  ];

  if (reviews === null) return <TableSkeleton rows={6} cols={5} />;
  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(2) : "0";

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card className="p-4"><MessageSquare className="w-5 h-5 text-primary mb-2" /><p className="text-2xl font-black font-display">{reviews.length}</p><p className="text-xs text-muted-foreground">Total reviews</p></Card>
        <Card className="p-4"><Star className="w-5 h-5 text-gold mb-2" /><p className="text-2xl font-black font-display">{avg}</p><p className="text-xs text-muted-foreground">Avg rating</p></Card>
        <Card className="p-4"><Star className="w-5 h-5 text-amber-500 mb-2" /><p className="text-2xl font-black font-display">{reviews.filter((r) => r.rating <= 2).length}</p><p className="text-xs text-muted-foreground">Low (≤2★)</p></Card>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
          <Input data-testid="reviews-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reviews…" className="pl-9 rounded-full" aria-label="Search reviews" />
        </div>
        <ExportMenu filename="reviews" title="Reviews" columns={cols} rows={filtered} testid="reviews-export" />
      </div>

      <Card className="p-0 overflow-hidden shadow-sm">
        <Table>
          <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Buyer</TableHead><TableHead>Rating</TableHead><TableHead className="hidden md:table-cell">Comment</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.review_id} data-testid={`review-row-${r.review_id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg overflow-hidden bg-muted shrink-0 flex items-center justify-center">
                      {r.product_image ? <img src={r.product_image} alt="" className="w-full h-full object-cover" /> : <MessageSquare className="w-4 h-4 text-muted-foreground" />}
                    </div>
                    <span className="text-sm truncate max-w-[180px]">{r.product_title}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm">{r.user_name}{r.verified_purchase && <Badge variant="outline" className="ml-1.5 text-[10px]">verified</Badge>}</TableCell>
                <TableCell><Stars n={r.rating} /></TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground max-w-[280px] truncate">{r.comment || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" disabled={busy === r.review_id} onClick={() => del(r.review_id)} data-testid={`review-del-${r.review_id}`} aria-label="Delete review"><Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground text-sm">{reviews.length === 0 ? "No reviews yet." : "No reviews match your search."}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </motion.div>
  );
};
